// nettiAsk — one place on a page asks netti something, in the person's words.
//
// The dock owns the one conversation (NettiDock → useNettiChat). Anything else
// that wants to start it (the connections space asking "what do you want to
// connect?", a docs page, a guide) does not build a parallel chat: it calls
// `askNetti(text)`, and the dock opens its rail on the thread and sends the text
// as the person's own message. Same transport, same rate ladder, same record.
//
// The event carries a mutable detail so the caller learns, synchronously, what
// happened to its ask: the dock writes `outcome` before dispatchEvent returns.
//
//   sent    the rail is open and the ask is on its way
//   opened  the rail is open, nothing was sent (netti is resting, or not public yet)
//   busy    netti is mid-answer: the rail is open, the ask was not sent
//   none    nobody answered: no dock on this page (or a phone, where there is no rail)
//
// Pure apart from `window`, so the contract is tested without a browser.

export const NETTI_ASK_EVENT = "innernet:netti-ask";

export type NettiAskOutcome = "sent" | "opened" | "busy" | "none";

export interface NettiAskDetail {
  text: string;
  /** written by the dock before the event returns */
  outcome?: NettiAskOutcome;
}

/** the longest ask the dock will send (the composer is a chat line, not a document) */
export const ASK_MAX = 600;

/** the ask as it will be sent: control characters out, runs of space folded, ends trimmed, cut to length */
export function cleanAsk(raw: unknown): string {
  if (typeof raw !== "string") return "";
  // eslint-disable-next-line no-control-regex
  const text = raw.replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, " ").replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n").trim();
  return text.length <= ASK_MAX ? text : text.slice(0, ASK_MAX).trimEnd();
}

/** Ask netti. Opens the rail and sends `text` as the person's words; says what came of it. */
export function askNetti(text: string): NettiAskOutcome {
  if (typeof window === "undefined") return "none";
  const clean = cleanAsk(text);
  if (!clean) return "none";
  const detail: NettiAskDetail = { text: clean };
  window.dispatchEvent(new CustomEvent<NettiAskDetail>(NETTI_ASK_EVENT, { detail }));
  return detail.outcome ?? "none";
}
